/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-study-treatment-arms-grid.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect } from 'react';
import { orderBy } from '@progress/kendo-data-query';

import ExpandableGridV2 from '@components/core/expandable/grid-v2';
import { GridColumnDateFilterV2 } from '@components/core/grid-filters-v2';
import { ExtraMenuItem, ExtraMenuItemPosition } from '@components/core/grid-header/grid-menu/grid-menu.interface';
import { useGridCells, useTranslation } from 'hooks';
import { StudyTreatmentArmFields } from 'queries/generated/fragments.generated';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';

import '@pages/admin/admin.scss';

const AdminStudyTreatmentArmsGrid = ({
    treatmentArmsData,
    treatmentArms,
    setTreatmentsArms,
    addTreatmentDialog,
    setCurrentTreatment,
    canEdit,
    isSysAdmin,
}: {
    treatmentArmsData: any[];
    treatmentArms: any[];
    setTreatmentsArms: Function;
    addTreatmentDialog: any;
    setCurrentTreatment: Function;
    canEdit: boolean;
    isSysAdmin: boolean;
}) => {
    const { t } = useTranslation();
    const { YesNoCell, NumberCell, DateCell, MaxLengthCell } = useGridCells();

    useEffect(() => {
        setTreatmentsArms(treatmentArmsData);
        let array = [...treatmentArmsData];
        array = restructuredUpdatedCreatedBy(array);
        array = orderBy(array, [{ field: 'name', dir: 'asc' }]) ?? [];
        setTreatmentsArms(array);
    }, [treatmentArmsData]);

    const extraMenuItems: ExtraMenuItem[] = [
        {
            position: ExtraMenuItemPosition.right,
            tooltipTitle: t('admin:addTreatment'),
            onClick: () => addTreatmentDialog.setTrue(),
            customClassName: 'grid-header__item--always-visible',
            disabled: !canEdit,
            icon: ['fal', 'plus'],
        },
    ];

    return (
        <ExpandableGridV2
            className="svt-grid-vertical-scroll"
            title={t('admin:treatmentArms')}
            customKey="treatment-arms-grid"
            id="treatmentArmsGrid"
            key="treatmentArmsGrid"
            items={treatmentArms}
            hideCheckbox
            hideExpand
            scrollable="scrollable"
            groupable
            reorderable
            resizable
            showPager
            pageable={false}
            rowActions={(item: StudyTreatmentArmFields) => [
                {
                    label: 'admin:editTreatmentArm',
                    icon: ['fal', 'edit'],
                    onClick: () => {
                        setCurrentTreatment(item);
                        addTreatmentDialog.setTrue();
                    },
                    enabled: canEdit || isSysAdmin,
                },
            ]}
            extraMenuItems={extraMenuItems}
            columns={[
                {
                    label: t('admin:sourceSystemId'),
                    id: 'ta-subjectCode',
                    field: 'subjectCode',
                    minWidth: 200,
                    lockedByDefault: true,
                    cells: { data: MaxLengthCell(30) },
                },
                {
                    label: t('common:name'),
                    id: 'ta-name',
                    field: 'name',
                    minWidth: 160,
                    lockedByDefault: true,
                    cells: { data: MaxLengthCell(60) },
                },
                {
                    label: t('common:description'),
                    field: 'description',
                    id: 'ta-description',
                    minWidth: 190,
                    cells: { data: MaxLengthCell(150) },
                },
                {
                    label: t('common:active'),
                    field: 'active',
                    id: 'ta-active',
                    minWidth: 160,
                    cells: { data: YesNoCell },
                    filterUi: GridColumnMenuActiveFilter,
                },
                {
                    label: t('ra:createdBy'),
                    id: 'ta-createdby',
                    field: '_createdByString',
                    minWidth: 180,
                },
                {
                    label: t('ra:createdDate'),
                    id: 'ta-created',
                    minWidth: 200,
                    field: 'created',
                    cells: { data: DateCell },
                    filterUi: GridColumnDateFilterV2,
                },
                {
                    label: t('ra:updatedBy'),
                    id: 'ta-updatedby',
                    field: '_updatedByString',
                    minWidth: 180,
                },
                {
                    label: t('ra:updatedDate'),
                    field: 'updated',
                    id: 'ta-updated',
                    minWidth: 200,
                    cells: { data: DateCell },
                    filterUi: GridColumnDateFilterV2,
                },
                {
                    field: 'recordId',
                    label: t('id'),
                    minWidth: 150,
                    id: 'ta-id',
                    filter: 'numeric',
                    cells: { data: NumberCell },
                    showByDefault: false,
                },
            ]}
        />
    );
};

export default AdminStudyTreatmentArmsGrid;
